import { motion } from "motion/react";
import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greenMui,
  greyMui,
  MOBILE_BREAKPOINT,
  orangeMui,
  ScreenDepth,
} from "@ryde/theme";

import {
  HEADER_STATION_NAME_COLOR,
  HEADER_STATION_NAME_FONT_WEIGHT,
} from "../../header-station-name.constants";

/** Grid columns are driven by motion style (gridTemplateColumns) for smooth resize. */
export const CourseDrawerWrapper = styled(motion.div).attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "course-drawer-wrapper",
})`
  display: grid;
  grid-template-areas:
    "header    header     header     header"
    "lhandle   form       mhandle    map"
    "lhandle   footer     mhandle    map";
  grid-template-rows: 67px 1fr 64px;
  height: 100%;

  .CourseDrawer-Header {
    grid-area: header;
  }

  .CourseDrawer-Form {
    grid-area: form;
  }

  .CourseDrawer-Footer {
    grid-area: footer;
  }

  .CourseDrawer-Map {
    grid-area: map;
  }

  .CourseDrawer-LeftHandle,
  .CourseDrawer-MiddleHandle {
    background-color: ${greyMui._300};
    cursor: col-resize;
    user-select: none;
    z-index: ${ScreenDepth.GRID_SPLITTER};
    position: relative;

    &:hover {
      background-color: ${greyMui._400};
    }

    &:focus-visible {
      outline: 2px solid ${blueMui._500};
      outline-offset: -2px;
    }
  }

  .CourseDrawer-LeftHandle {
    grid-area: lhandle;
  }

  .CourseDrawer-MiddleHandle {
    grid-area: mhandle;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
    flex-direction: column;

    .CourseDrawer-LeftHandle,
    .CourseDrawer-MiddleHandle {
      display: none;
    }
  }
`;

export const CourseTabsWrapperStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "course-tabs-wrapper",
})`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: auto;
`;

export const CourseTabsEarsContainer = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "course-tabs-ears-container",
})`
  padding-top: 12px;
  padding-bottom: 12px;
  padding-inline-start: 22px;

  transition:
    filter 0.55s ease-out,
    backdrop-filter 0.55s ease-out;
  &.inner-item-editing {
    filter: blur(2px) grayscale(100%);
    backdrop-filter: blur(3px);
    pointer-events: none;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-inline-start: 12px;
  }
`;

export const CourseTabsErrorContainer = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "course-tabs-errors-container",
})`
  padding-inline-end: 18px;
`;

export const CourseTabsTabStationsStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "course-tab-stations",
})`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  padding-inline-start: 22px;
  padding-inline-end: 22px;
  margin-top: 12px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-inline-start: 12px;
    padding-inline-end: 12px;
    width: 100%;
  }
`;

export const CourseTabsContentWrapperStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "course-tab-content",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  padding-inline-start: 22px;
  padding-inline-end: 22px;
  margin-top: 12px;
  width: 85%;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: calc(100% - 30px);
    padding-inline-start: 12px;
  }
`;

export const CourseBasicTabInputs = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "course-basic-tab-inputs",
})`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 15px;
  margin-bottom: 20px;
  .template_wrapper:nth-child(3) {
    grid-column: 1 / 3;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    row-gap: 15px;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr 1fr;
  }
`;

export const CourseMapWrapperStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "course-map-wrapper",
})`
  height: 100%;
  width: 100%;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-inline-start: 10px;
  }
`;

export const CourseMapStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "course-map",
})`
  height: calc(100vh - 62px);
  position: sticky;
  top: 62px;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: calc(100vh - 232px);
  }
`;

export const CourseTabsBottomBlocksStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "course-tabs-bottom",
  },
)`
  margin-top: auto;
  padding-bottom: 14px;
  padding-inline-start: 22px;
`;

export const CourseBottomBlockStyled = styled.span.attrs<IStyledAttributes>({
  "data-e2e": "course-bottom-block",
})`
  color: ${blueGreyMui._500};
  display: inline-flex;
  font-size: 12px;
  padding-inline-end: 7px;
  gap: 4px;
  align-items: baseline;

  label {
    color: ${greyMui._500};
  }
  span {
    color: ${blueGreyMui._700};
    font-weight: 500;
  }

  .link_clickable {
    cursor: pointer;
    &:hover {
      color: ${blueGreyMui._900};
      text-decoration: underline;
    }
  }

  &:not(&:last-child) {
    &:after {
      content: "";
      border-inline-end: 1px solid ${blueGreyMui._500};
      height: 8px;
      margin-inline-start: 8px;
      display: inline-block;
    }
  }
`;

export const CourseEditTopInfoContainerStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "course-edit-top-info-container",
  },
)`
  .info_item {
    padding-inline-start: 22px;
    padding-inline-end: 25px;
    padding-top: 5px;
    padding-bottom: 5px;
    &:first-child {
      padding-top: 10px;
    }

    p {
      font-size: 14px;
      font-weight: 500;
      padding: 5px;
      color: ${orangeMui._800};
      background-color: ${orangeMui._50};
      outline: 1px solid ${orangeMui._100};
      border-radius: 4px;
    }
  }
`;

export const CourseDirectionTemplateBannerStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "course-direction-template-banner-wrapper",
  },
)<{ $isBDirectionTemplate: boolean }>`
  padding-inline-start: 22px;
  padding-inline-end: 25px;
  padding-top: 10px;
  padding-bottom: 2px;

  .direction-template-label {
    margin: 0;
    padding: 6px 10px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    color: ${({ $isBDirectionTemplate }) =>
      $isBDirectionTemplate ? blueMui._800 : greenMui._800};
    background-color: ${({ $isBDirectionTemplate }) =>
      $isBDirectionTemplate ? blueMui._50 : greenMui._50};
    outline: 1px solid
      ${({ $isBDirectionTemplate }) =>
        $isBDirectionTemplate ? blueMui._100 : greenMui._100};
    border-radius: 999px;
    text-align: center;
  }

  .direction-template-availability-note {
    margin: 6px 0 0;
    padding: 0 10px 4px;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.35;
    color: ${greyMui._600};
    text-align: center;

    .direction-template-station-name {
      color: ${HEADER_STATION_NAME_COLOR};
      font-weight: ${HEADER_STATION_NAME_FONT_WEIGHT};
    }
  }
`;
